import { useCallback, useEffect, useMemo } from 'react'

import {
  QUERY_KEY_MANAGEMENT_SEARCH_UNIT_TASK_GROUPS,
  QUERY_KEY_MANAGEMENT_SEARCH_UNIT_TASK_GROUPS_INFINITE,
  QUERY_MANAGEMENT_GET_UNIT_TASK_GROUP
} from '@apps-common/apis/management/unit-task/task-groups'
import { getUnitTaskGroup } from '@apps-common/apis/management/unit-task/task-groups/get-unit-task-group'
import { UNIT_TASK_UPDATE_LISTENER_STATUSES } from '@apps-common/config/unit-task'
import { useTimerCallback } from '@apps-common/hooks/useTimerCallback'
import { queryClient } from '@apps-common/lib'
import { IUnitTaskGroupModel } from '@apps-common/types/portal/unit-tasks'

import { useTaskGroupNotificationListener } from './useTaskGroupNotificationListener'

export const useTaskGroupUpdates = (containerId: string, tasksLoading: boolean, allLoading: boolean, tasks: IUnitTaskGroupModel[]) => {
  useEffect(() => {
    const element = document.getElementById(containerId)

    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
  }, [tasksLoading])

  const updateTaskGroupInCache = useCallback((updated: IUnitTaskGroupModel) => {
    queryClient.setQueryData(QUERY_MANAGEMENT_GET_UNIT_TASK_GROUP(updated.id), updated)

    const listQueries = queryClient.getQueryCache().findAll({ queryKey: QUERY_KEY_MANAGEMENT_SEARCH_UNIT_TASK_GROUPS() })
    listQueries.forEach((query) => {
      queryClient.setQueryData(query.queryKey, (data: IUnitTaskGroupModel[] | undefined) => {
        if (!Array.isArray(data)) {
          return data
        }

        const index = data.findIndex((item) => item.id === updated.id)
        if (index === -1) {
          return data
        }

        return data.map((item) => (item.id === updated.id ? updated : item))
      })
    })

    const infiniteQueries = queryClient.getQueryCache().findAll({ queryKey: QUERY_KEY_MANAGEMENT_SEARCH_UNIT_TASK_GROUPS_INFINITE() })
    infiniteQueries.forEach((query) => {
      queryClient.setQueryData(query.queryKey, (data: any) => {
        if (!data || !Array.isArray(data.pages)) {
          return data
        }

        const pages = data.pages.map((page: IUnitTaskGroupModel[]) => {
          if (!Array.isArray(page)) {
            return page
          }

          const index = page.findIndex((item) => item.id === updated.id)
          if (index === -1) {
            return page
          }

          return page.map((item) => (item.id === updated.id ? updated : item))
        })

        return { ...data, pages }
      })
    })
  }, [])

  const refreshCb = useCallback(
    async (taskGroupId: number) => {
      try {
        const updated = await queryClient.fetchQuery({
          queryKey: QUERY_MANAGEMENT_GET_UNIT_TASK_GROUP(taskGroupId),
          queryFn: () => getUnitTaskGroup(taskGroupId)
        })

        if (updated) {
          updateTaskGroupInCache(updated)
        }
      } catch (error) {
        queryClient.refetchQueries({ queryKey: QUERY_MANAGEMENT_GET_UNIT_TASK_GROUP(taskGroupId) })
      }
    },
    [updateTaskGroupInCache]
  )

  const pendingTasks = useMemo(() => {
    if (tasksLoading || allLoading) {
      return []
    }

    return tasks?.filter((q) => UNIT_TASK_UPDATE_LISTENER_STATUSES.includes(q.status)) || []
  }, [allLoading, tasks, tasksLoading])

  const getIntervalMs = (count: number) => {
    const extraDelay = Math.min(Math.max(count - 1, 0) * 1000, 30000)
    return 15000 + extraDelay
  }

  const processQueue = useCallback(() => {
    const run = async () => {
      const staggerDelayMs = Math.min(1000 + pendingTasks.length * 250, 5000)

      for (let i = 0; i < pendingTasks.length; i += 1) {
        const task = pendingTasks[i]
        await refreshCb(task.id)
        await new Promise((resolve) => setTimeout(resolve, staggerDelayMs))
      }
    }

    void run()
  }, [pendingTasks, refreshCb])

  useTimerCallback(processQueue, getIntervalMs(pendingTasks.length), pendingTasks.length > 0)
  useTaskGroupNotificationListener(pendingTasks || [], (event) => {
    const taskGroupId = parseInt(event?.data?.taskGroupId || '', 10)

    if (!Number.isNaN(taskGroupId)) {
      console.log('[notif-update] task-group refresh', taskGroupId)
      const updatedStatus = (event?.data?.status || event?.data?.action || '').toString()
      if (updatedStatus && pendingTasks.some((task) => task.id === taskGroupId)) {
        updateTaskGroupInCache({ ...(pendingTasks.find((task) => task.id === taskGroupId) as IUnitTaskGroupModel), status: updatedStatus })
      }
      refreshCb(taskGroupId)
      return
    }

    processQueue()
  })
}
